import { useEffect, useRef, useState } from "react";
import ProductEntry from "./product-entry";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridColumnMenuFilter,
  GridColumnMenuProps,
  GridDataStateChangeEvent,
  GridExpandChangeEvent,
  GridNoRecords,
} from "@progress/kendo-react-grid";

import {
  setExpandedState,
  setGroupIds,
} from "@progress/kendo-react-data-tools";
import { DataResult, process, State } from "@progress/kendo-data-query";
import { useGetProducts } from "../../apiclient/product/useGetProductApi";
import { url } from "inspector";
import CategoryModel from "../../models/masters/category-model";
import { SubmitStatus } from "../../core/formik-model-dialog.component";
import { API_END_POINT } from "../../constants";
import { GridCommandCell } from "../../core/grid-command-cell.component";
import { LoadingIndicator } from "../../core/loading-indicator.component";
import {
  DeleteModelDialog,
  DeletePostDataInfo,
  DeleteStatus,
} from "../../core/delete-model-dialog.component";
import ProductModel from "../../models/products/product-model";
import { GridActionComponent } from "../../core/action.title.component";

export const addOrUpdateArrayItem = (data: any[], item: any, key: string) => {
  const newData = data ? [...data] : [];
  const index = data?.findIndex((d) => d[key] === item[key]);
  if (index > -1) {
    newData.splice(index, 1, item);
  } else {
    newData.push(item);
  }
  return newData;
};

const ProductPage: React.FC = () => {
  const initialDataState: State = {
    take: 10,
    skip: 0,
    group: [{ field: "mainGroupName" }],
  };
  const [dataState, setDataState] = useState<State>(initialDataState);
  const [deleteproductData, setDeleteProductData] =
    useState<DeletePostDataInfo>();
  const [collapsedState, setCollapsedState] = useState<string[]>([]);
  const [resultData, setResultData] = useState<DataResult>();

  const [editModal, setEditModal] = useState<boolean>(false);
  const [editData, setEditdata] = useState<ProductModel>(null);
  const exportExcel = useRef<ExcelExport | null>(null);
  const [productShow, setProductShow]=useState<boolean>(false);
  const [isViewMode, setViewMode] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState(false);


  const [pgreset, setpgreset] = useState<boolean>(false);
  
  const {
    data: result,
    isLoading,
    isError,
    refetch: reloadProduct,
  } = useGetProducts();

  const processWithGroups = (data: ProductModel[], dataState: State) => {
    const newDataState = process(data, dataState);
    setGroupIds({ data: newDataState.data, group: dataState.group });
    return newDataState;
  };

  const fetchData = async () => {
    try {
      setIsRefreshing(true);
      const { data, error } = await reloadProduct();
      if (error) {
        console.error("Error reloading:", error);
      } else {
        console.log("Reloaded data:", data);
      }
    } catch (err) {
      console.error("Unexpected error:", err);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    setEditdata(null);
    fetchData();
    if (result) {
      setResultData(processWithGroups(result, initialDataState));
    }
  }, [result]);

  let newData: any = null;
  if (resultData) {
    newData = setExpandedState({
      data: resultData.data,
      collapsedIds: collapsedState,
    });
  }

  const onSaveComplete = (status: SubmitStatus, data: ProductModel) => {
    setEditdata(null);
    if (status === SubmitStatus.Completed) {
      
      reloadProduct();
      setResultData(processWithGroups(newData, dataState));
    }
    setProductShow(false);
  };

  const excelExport = () => {
    if (exportExcel.current !== null) {
      exportExcel.current.save();
    }
  };

  const onAddNew = () => {
    setEditModal(true);
    setEditdata(new ProductModel());
    setProductShow(true)
  };

  const onEdit = (data: ProductModel) => {
    setEditModal(true);
    setEditdata(data);
    setProductShow(true);
  };
  const onView = (data: ProductModel) => {
    setViewMode(true);
    setEditdata(data);
  };

  const onRemove = (data: ProductModel) => {
    const { productId } = data;
    setDeleteProductData({
      data,
      route: `Product/DeleteProduct/${productId}`,
      endpoint: API_END_POINT.PRODUCT,
    });
    reloadProduct();
  };
  const onDeleteComplete = (status: DeleteStatus, data: ProductModel) => {
    if (status === DeleteStatus.Completed) {
      reloadProduct();
      setResultData(processWithGroups(result, dataState));
    }
    setDeleteProductData(null);
  };
  const ColumnMenu = (props: GridColumnMenuProps) => {
    return (
      <div>
        <GridColumnMenuFilter {...props} expanded={true} />
      </div>
    );
  };

  const CommandCell = (props: GridCellProps) => {
    // Check if the current row is a group header row
    if (props.dataItem.hasOwnProperty("aggregates")) {
      return <td></td>; // Return an empty cell to hide the buttons
    }
  
    return (
      <GridCommandCell
        {...props}
        edit={onEdit}
        remove={onRemove}
        add={onAddNew}
        view={onView}
        dataItem={props.dataItem}
        idPropertyName="productId"
        editPermissionName={null}
        deletePermissionName={null}
      />
    );
  };
  

  const onSearch = (e: any) => {
    if (e.target.value.length > 0) {
      const searchKey: string = e.target.value;
      const filteredData = result?.filter(
        (p) =>
          p?.productName?.toLowerCase().includes(searchKey?.toLowerCase()) ||
          p?.productShortName?.toLowerCase().includes(searchKey?.toLowerCase())
      );
      setResultData(process(filteredData, dataState));
    } else {
      setResultData(process(result, dataState));
    }
  };

  const expandChange = (event: GridExpandChangeEvent) => {
    const item = event.dataItem;

    if (item.groupId) {
      const newCollapsedIds = !event.value
        ? [...collapsedState, item.groupId]
        : collapsedState.filter((groupId) => groupId !== item.groupId);
      setCollapsedState(newCollapsedIds);
    }
  };

  const refreshGrid = () => {
    reloadProduct();
  };
  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const newDataState = processWithGroups(result, event.dataState);
    setResultData(newDataState);
    setDataState(event.dataState);
  };

  const ColumnFormatBoolean = (props: any) => {
    return (
      <td
        {...props.tdProps}
        colSpan={1}
        style={{
          color: "black",
        }}
      >
        {props.children === "true" ? (
          <span style={{ color: "green", fontWeight: "bold" }}>Yes</span>
        ) : (
          <span style={{ color: "red", fontWeight: "bold" }}>No</span>
        )}
      </td>
    );
  };
  const onAddClick = () => {
    setEditModal(true);
    setViewMode(false);
    setEditdata(new ProductModel());

  };
  let grid = null;
  if (result) {
    grid = (
      <Grid
        data={newData}
        pageable={{ pageSizes: [10, 20, 50, 100] }}
        total={resultData?.total}
        groupable={true}
        sortable={true}
        onDataStateChange={dataStateChange}
        {...dataState}
        onExpandChange={expandChange}
        columnVirtualization={false}
        resizable={true}
      >
        <GridColumn
          field="code"
          title="Code"
          columnMenu={ColumnMenu}
        />
        <GridColumn
          field="productShortName"
          title="Short Name"
          columnMenu={ColumnMenu}
        />
        <GridColumn
          field="productName"
          title="Product Name"
          columnMenu={ColumnMenu}
        />

        <GridColumn
          field="hsnCode"
          title="HSN Code"
        />
        <GridColumn  field="mainGroupName" title="Main Group" />

        <GridColumn
          field="isActive"
          title="Active"
          cells={{ data: ColumnFormatBoolean }}
        />
        <GridColumn
          field="isIndent"
          title="Indent"
          cells={{ data: ColumnFormatBoolean }}
        />
        <GridColumn
          field="isCard"
          title="Card"
          cells={{ data: ColumnFormatBoolean }}
        />
        <GridColumn
          field="isFree"
          title="Free"
          cells={{ data: ColumnFormatBoolean }}
        />
        <GridColumn
          cell={CommandCell}
          title="Action"
          width={"150"}
          groupable={true}
        />
        <GridNoRecords>
          <div className="mt-2" style={{ width: "100%", height: "500px" }}>
            <center>
              <h5>No data found</h5>
            </center>
          </div>
        </GridNoRecords>
      </Grid>
    );
    if (isError) {
      return (
        <div>
          <h4>Error Loadong Page..</h4>
        </div>
      );
    }
    return (
      <>
        <div
          style={{ minHeight: "600px" }}
          className="card p-3 border-0 m-3 sw-shadow radius-15"
        >
          <div>
            <GridActionComponent
              onSearchClick={onSearch}
              addButtonClick={onAddClick}
              excelExportClick={excelExport}
              refreshClick={fetchData}
              title="PRODUCT"
              addButtonTooltip="Add Product"
            ></GridActionComponent>
          </div>

          {editModal && (
            <ProductEntry isViewMode={isViewMode} data={editData} onClose={onSaveComplete} />
          )}
             {isViewMode && (
            <ProductEntry
              isViewMode={true}
              data={editData}
              onClose={onSaveComplete}
            />
          )}
          {deleteproductData && (
            <DeleteModelDialog
              title="Delete Product"
              deleteData={deleteproductData}
              onClose={onDeleteComplete}
            >
              <div>
                Are you sure you want to delete Product :{" "}
                <span style={{ fontWeight: "bold" }}>
                  {deleteproductData.data.productName}
                </span>
                ?
              </div>
            </DeleteModelDialog>
          )}

            <div>
            <>
              {isRefreshing || isLoading ? (
                <LoadingIndicator isLoading={isRefreshing}></LoadingIndicator>
              ) : (
                <div>
                  {grid}
                  <ExcelExport
                fileName={"Product_List"}
                data={result}
                ref={exportExcel}
              >
                <ExcelExportColumn width={100} field="code" title=" Code" />
                <ExcelExportColumn
                  width={100}
                  field="hsnCode"
                  title=" HSNCode"
                />
                <ExcelExportColumn
                  width={150}
                  field="productName"
                  title="ProductName"
                />
                <ExcelExportColumn
                  width={150}
                  field="productShortName"
                  title="ProductShortName"
                />
                <ExcelExportColumn
                  width={170}
                  field="mainGroupName"
                  title="MainGroupName"
                />
                <ExcelExportColumn
                  width={150}
                  field="unitQty"
                  title="UnitQty"
                />
                <ExcelExportColumn
                  width={280}
                  field="unitFactor"
                  title="UnitFactor"
                />
                <ExcelExportColumn
                  width={280}
                  field="baseUnitName"
                  title="BaseUnitName"
                />
                <ExcelExportColumn
                  width={280}
                  field="subUnitName"
                  title="SubUnitName"
                />
                <ExcelExportColumn
                  width={280}
                  field="subGroupName"
                  title="Sub Group Name"
                />
                <ExcelExportColumn
                  width={280}
                  field="packetPerTray"
                  title="PacketPerTray"
                />
                <ExcelExportColumn
                  width={280}
                  field="printingOrder"
                  title="PrintingOrder"
                />
                <ExcelExportColumn
                  width={280}
                  field="baseRate"
                  title="BaseRate"
                />
                <ExcelExportColumn
                  width={280}
                  field="cgstRate"
                  title="CGSTRate"
                />
                <ExcelExportColumn
                  width={280}
                  field="sgstRate"
                  title="SGSTRate"
                />
                <ExcelExportColumn
                  width={280}
                  field="igstRate"
                  title="IGSTRate"
                />
                <ExcelExportColumn
                  width={280}
                  field="netRate"
                  title="NetRate"
                />
                <ExcelExportColumn
                  width={280}
                  field="cardRate"
                  title="CardRate"
                />
                <ExcelExportColumn
                  width={280}
                  field="freeRate"
                  title="FreeRate"
                />
                {/* <ExcelExportColumn
                  width={280}
                  field="literPerCrate"
                  title="LiterPerCrate"
                />{" "} */}
                <ExcelExportColumn
                  width={280}
                  field="    specialOrderRate"
                  title="SpecialOrderRate"
                />
                <ExcelExportColumn
                  width={280}
                  field="isActive"
                  title="IsActive"
                />
                <ExcelExportColumn
                  width={280}
                  field="isIndent"
                  title="IsIndent"
                />
                <ExcelExportColumn width={280} field="isCard" title="IsCard" />
                <ExcelExportColumn width={280} field="isFree" title="IsFree" />
                <ExcelExportColumn
                  width={280}
                  field="isSpecialOrder"
                  title="IsSpecialOrder"
                />
                <ExcelExportColumn width={280} field="isCan" title="IsCan" />
                {/* <ExcelExportColumn
                  width={280}
                  field="isUnitRate"
                  title="IsUnitRate"
                /> */}
                <ExcelExportColumn
                  width={280}
                  field="isCommission"
                  title="IsCommission"
                />
                <ExcelExportColumn
                  width={280}
                  field="minimumOrderQty"
                  title="MinimumOrderQty"
                />
                <ExcelExportColumn
                  width={280}
                  field="maximumOrderQty"
                  title="MaximumOrderQty"
                />
                <ExcelExportColumn
                  width={280}
                  field="orderinMultiplesOf"
                  title="OrderinMultiplesOf"
                />
                {/* <ExcelExportColumn
                  width={280}
                  field="wrapperWeight"
                  title="WrapperWeight"
                />{" "}
                <ExcelExportColumn
                  width={280}
                  field="productWeight"
                  title="ProductWeight"
                /> */}
              </ExcelExport>
                  </div>
              )}
            </>
          </div>
          <div></div>
        </div>
      </>
    );
  }
};
export default ProductPage;
